import { ReactNode } from 'react';
import { usePlatformStatus } from '@/contexts/PlatformStatusContext';
import { useAdminRole } from '@/hooks/useAdminRole';
import { Logo } from './Logo';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface MaintenanceGuardProps {
  children: ReactNode;
}

export const MaintenanceGuard = ({ children }: MaintenanceGuardProps) => {
  const { maintenanceMode, loading: statusLoading } = usePlatformStatus();
  const { isAdmin, loading: adminLoading } = useAdminRole();

  // While loading, render nothing to avoid flash
  if (statusLoading || adminLoading) {
    return <div className="min-h-screen bg-background" />;
  }

  // If not in maintenance mode or user is admin, render children normally
  if (!maintenanceMode || isAdmin) {
    return <>{children}</>;
  }

  // Maintenance mode active + non-admin user → show maintenance page
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
      <Logo size="lg" />
      <div className="mt-8 flex items-center gap-3 text-amber-500">
        <AlertTriangle className="w-8 h-8" />
        <h1 className="text-2xl md:text-3xl font-display font-bold">Under Maintenance</h1>
      </div>
      <p className="mt-4 text-muted-foreground max-w-md text-base md:text-lg">
        We're currently performing scheduled maintenance. Please check back shortly.
      </p>
      <Link
        to="/auth"
        className="mt-8 text-sm text-primary underline underline-offset-4 hover:text-primary/80"
      >
        Go to Login
      </Link>
    </div>
  );
};
